
import { Card } from "@/components/ui/card";
import { Calendar } from "@/components/ui/calendar";
import { useState, useEffect } from "react";
import { formatDate } from "@/utils/dateFormatters";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ExternalLink, CalendarIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { safeParseDate } from "@/utils/dateValidation";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";

interface TenderCalendarProps {
  tenders: any[];
}

// Helper type guard function to check if a value is a record/object
function isObject(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

// Define deadline categories for better organization
type DeadlineCategory = 'tender' | 'application' | 'additional' | 'task';

interface DeadlineCategoryConfig {
  key: DeadlineCategory;
  label: string;
  color: string;
  icon: React.ReactNode;
  extractDate: (item: any) => Date | null;
  filterFn?: (item: any) => boolean; // Optional filter function to determine if a tender belongs to this category
}

export const TenderCalendar = ({ tenders }: TenderCalendarProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [date, setDate] = useState<Date | undefined>(new Date());
  const [activeTab, setActiveTab] = useState<DeadlineCategory | 'all'>('all');
  const [selectedDateItems, setSelectedDateItems] = useState<Record<DeadlineCategory, any[]>>({
    tender: [],
    application: [],
    additional: [],
    task: []
  });

  // Helper function to check if the tender's procedure type indicates it's an application deadline
  const isApplicationDeadlineProcedure = (tender: any): boolean => {
    // Get procedure type from tender data
    if (!tender || !tender.tender_data) return false;
    
    const procedureType = tender.tender_data["procedure-type"] || 
                          tender.tender_data["TOP_TYPE"] || 
                          tender.tender_data.procedure?.type;
    
    // Procedures that typically have application deadlines first
    const applicationProcedureTypes = [
      'restricted',
      'Restricted Procedure',
      'negotiated',
      'Competitive procedure with negotiation',
      'Competitive dialogue',
      'Innovation partnership',
      'Design contest',
      'With call for competition'
    ];
    
    // Check if procedure is in the application procedures list
    if (procedureType) {
      // Handle string or array cases
      if (typeof procedureType === 'string') {
        return applicationProcedureTypes.some(type => 
          procedureType.toLowerCase().includes(type.toLowerCase())
        );
      } else if (Array.isArray(procedureType) && procedureType.length > 0) {
        return applicationProcedureTypes.some(type => 
          procedureType[0].toLowerCase().includes(type.toLowerCase())
        );
      }
    }
    
    // If procedure type is not found, check if application deadline exists but tender deadline doesn't
    const hasApplicationDeadline = Boolean(
      tender.tender_data["deadline-receipt-request-date-lot"]?.[0] ||
      tender.tender_data["deadline-receipt-request"]?.[0] ||
      tender.tender_data["DD"]?.[0]
    );
    
    const hasTenderDeadline = Boolean(
      tender.tender_data["deadline-receipt-tender-date-lot"]?.[0] ||
      tender.tender_data["deadline-receipt-tender-time-lot"]?.[0] ||
      tender.tender_data["DT"]?.[0]
    );
    
    return hasApplicationDeadline && !hasTenderDeadline;
  };

  // Helper function to check if the tender's procedure type indicates it's a tender deadline (submission)
  const isTenderDeadlineProcedure = (tender: any): boolean => {
    if (!tender || !tender.tender_data) return true; // Default to tender deadline if can't determine
    
    const procedureType = tender.tender_data["procedure-type"] || 
                          tender.tender_data["TOP_TYPE"] || 
                          tender.tender_data.procedure?.type;
    
    // Procedures that typically only have tender submission deadlines
    const tenderProcedureTypes = [
      'open',
      'Open Procedure',
      'Negotiated procedure',
      'Negotiated with prior publication',
      'Negotiated without prior publication',
      'Award of a contract without prior publication'
    ];
    
    // Check if procedure is in the tender procedures list
    if (procedureType) {
      // Handle string or array cases
      if (typeof procedureType === 'string') {
        return tenderProcedureTypes.some(type => 
          procedureType.toLowerCase().includes(type.toLowerCase())
        );
      } else if (Array.isArray(procedureType) && procedureType.length > 0) {
        return tenderProcedureTypes.some(type => 
          procedureType[0].toLowerCase().includes(type.toLowerCase())
        );
      }
    }
    
    // If procedure type is not found but tender deadline exists, consider it a tender deadline
    const hasTenderDeadline = Boolean(
      tender.tender_data["deadline-receipt-tender-date-lot"]?.[0] ||
      tender.tender_data["deadline-receipt-tender-time-lot"]?.[0] ||
      tender.tender_data["DT"]?.[0]
    );
    
    return hasTenderDeadline;
  };

  // Configuration for each deadline category
  const deadlineCategories: DeadlineCategoryConfig[] = [
    {
      key: 'tender',
      label: t("dashboard.calendar.tenderDeadlines", "Tilbudsfrister"),
      color: 'bg-red-100 text-red-700 border-red-200',
      icon: <CalendarIcon className="h-4 w-4 text-red-500" />,
      filterFn: isTenderDeadlineProcedure,
      extractDate: (tender: any) => {
        if (!tender) return null;
        
        // For tenders with tender_data
        if (tender.tender_data) {
          // Try multiple possible deadline paths for tender submission deadlines
          const deadlineValues = [
            tender.tender_data["deadline-receipt-tender-date-lot"]?.[0],
            tender.tender_data["deadline-receipt-tender-time-lot"]?.[0],
            tender.tender_data["DT"]?.[0],
            tender.tender_data.deadline_date,
            tender.tender_data.deadline
          ];
          
          // Find first non-null value
          for (const deadlineValue of deadlineValues) {
            if (deadlineValue) {
              try {
                const parsedDate = safeParseDate(deadlineValue);
                if (parsedDate) {
                  return parsedDate;
                }
              } catch (e) {
                console.error("Error parsing tender deadline:", e);
              }
            }
          }
        }
        
        // For direct deadline properties
        if (tender.deadline_date) {
          try {
            const parsedDate = safeParseDate(tender.deadline_date);
            if (parsedDate) return parsedDate;
          } catch (e) {
            console.error("Error parsing deadline:", e);
          }
        }
        
        if (tender.deadline) {
          try {
            const parsedDate = safeParseDate(tender.deadline);
            if (parsedDate) return parsedDate;
          } catch (e) {
            console.error("Error parsing deadline:", e);
          }
        }
        
        return null;
      }
    },
    {
      key: 'application',
      label: t("dashboard.calendar.applicationDeadlines", "Ansøgningsfrister"),
      color: 'bg-blue-100 text-blue-700 border-blue-200',
      icon: <CalendarIcon className="h-4 w-4 text-blue-500" />,
      filterFn: isApplicationDeadlineProcedure,
      extractDate: (tender: any) => {
        if (!tender) return null;
        
        // Try to extract application deadline from tender data
        if (tender.tender_data) {
          const applicationDeadlineValues = [
            tender.tender_data["deadline-receipt-request-date-lot"]?.[0],
            tender.tender_data["deadline-receipt-request"]?.[0],
            tender.tender_data["DD"]?.[0]
          ];
          
          for (const deadlineValue of applicationDeadlineValues) {
            if (deadlineValue) {
              try {
                const parsedDate = safeParseDate(deadlineValue);
                if (parsedDate) return parsedDate;
              } catch (e) {
                console.error("Error parsing application deadline:", e);
              }
            }
          }
        }
        
        return null;
      }
    },
    {
      key: 'additional',
      label: t("dashboard.calendar.additionalInfoDeadlines", "Frist for supplerende oplysninger"),
      color: 'bg-amber-100 text-amber-700 border-amber-200',
      icon: <CalendarIcon className="h-4 w-4 text-amber-500" />,
      extractDate: (tender: any) => {
        if (!tender) return null;
        
        // Try to extract additional info deadline
        if (tender.tender_data) {
          const additionalInfoDeadlineValues = [
            tender.tender_data["deadline-date-lot"]?.[0],
            tender.tender_data["deadline"]?.[0],
          ];
          
          for (const deadlineValue of additionalInfoDeadlineValues) {
            if (deadlineValue) {
              try {
                const parsedDate = safeParseDate(deadlineValue);
                if (parsedDate) return parsedDate;
              } catch (e) {
                console.error("Error parsing additional info deadline:", e);
              }
            }
          }
        }
        
        return null;
      }
    },
    {
      key: 'task',
      label: t("dashboard.calendar.taskDeadlines", "Opgavefrister"),
      color: 'bg-green-100 text-green-700 border-green-200',
      icon: <CalendarIcon className="h-4 w-4 text-green-500" />,
      extractDate: (task: any) => {
        if (!task || !task.due_date) return null;
        
        try {
          const parsedDate = safeParseDate(task.due_date);
          if (parsedDate) return parsedDate;
        } catch (e) {
          console.error("Error parsing task deadline:", e);
        }
        
        return null;
      }
    }
  ];

  // Fetch tasks for all tenders
  const { data: tasks = [] } = useQuery({
    queryKey: ['tender-tasks', tenders.map(t => t.id).join(',')],
    queryFn: async () => {
      if (tenders.length === 0) return [];
      
      const { data, error } = await supabase
        .from('tender_tasks')
        .select(`
          *,
          assignee:profiles!tender_tasks_assignee_id_fkey(
            id,
            email,
            company_name
          )
        `)
        .in('tender_id', tenders.map(t => t.id))
        .not('due_date', 'is', null);

      if (error) throw error;
      return data;
    },
    enabled: tenders.length > 0
  });

  // When tenders or tasks change, re-check the selected date to update what's visible
  useEffect(() => {
    if (date) {
      updateSelectedDateItems(date);
    }
  }, [tenders, tasks]);

  const handleTenderClick = (tender: any) => {
    navigate(`/offer-management/${tender.id}`);
  };

  const handleTaskClick = (task: any) => {
    navigate(`/offer-management/${task.tender_id}`);
  };

  // Get dates with deadlines for all categories
  const getDatesWithDeadlines = () => {
    const dates: Date[] = [];
    
    // Process tenders for different deadline types
    tenders.forEach(tender => {
      // Add tender submission deadlines
      const tenderDeadlineDate = deadlineCategories[0].extractDate(tender);
      if (tenderDeadlineDate && deadlineCategories[0].filterFn?.(tender) !== false) {
        dates.push(tenderDeadlineDate);
      }
      
      // Add application deadlines
      const applicationDeadlineDate = deadlineCategories[1].extractDate(tender);
      if (applicationDeadlineDate && deadlineCategories[1].filterFn?.(tender) !== false) {
        dates.push(applicationDeadlineDate);
      }
      
      // Add additional info deadlines
      const additionalInfoDeadlineDate = deadlineCategories[2].extractDate(tender);
      if (additionalInfoDeadlineDate) {
        dates.push(additionalInfoDeadlineDate);
      }
    });

    // Add task deadlines
    tasks.forEach(task => {
      if (task.due_date) {
        try {
          const taskDate = safeParseDate(task.due_date);
          if (taskDate) {
            dates.push(taskDate);
          }
        } catch (e) {
          console.error("Error processing task date:", e);
        }
      }
    });

    return dates;
  };

  // Get dates for specific category
  const getCategoryDates = (category: DeadlineCategory) => {
    const dates: Date[] = [];
    
    if (category === 'task') {
      // Handle tasks
      tasks.forEach(task => {
        const taskDate = deadlineCategories[3].extractDate(task);
        if (taskDate) {
          dates.push(taskDate);
        }
      });
    } else {
      // Handle tender-related deadlines
      tenders.forEach(tender => {
        const categoryConfig = deadlineCategories.find(c => c.key === category);
        if (categoryConfig) {
          const deadlineDate = categoryConfig.extractDate(tender);
          // Apply filter function if it exists
          if (deadlineDate && (categoryConfig.filterFn?.(tender) !== false)) {
            dates.push(deadlineDate);
          }
        }
      });
    }
    
    return dates;
  };

  // Check if two dates are the same day
  const isSameDay = (date1: Date, date2: Date): boolean => {
    return (
      date1.getDate() === date2.getDate() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getFullYear() === date2.getFullYear()
    );
  };

  // Update the selected date items based on current date
  const updateSelectedDateItems = (selectedDate: Date) => {
    // Create an object to store items for each category
    const itemsByCategory: Record<DeadlineCategory, any[]> = {
      tender: [],
      application: [],
      additional: [],
      task: []
    };
    
    // Filter tenders for tender deadlines on the selected date
    // Apply both date matching AND the category's filter function
    itemsByCategory.tender = tenders.filter(tender => {
      const deadlineDate = deadlineCategories[0].extractDate(tender);
      const matchesDate = deadlineDate ? isSameDay(deadlineDate, selectedDate) : false;
      const passesFilter = deadlineCategories[0].filterFn ? deadlineCategories[0].filterFn(tender) : true;
      return matchesDate && passesFilter;
    });
    
    // Filter tenders for application deadlines on the selected date
    itemsByCategory.application = tenders.filter(tender => {
      const deadlineDate = deadlineCategories[1].extractDate(tender);
      const matchesDate = deadlineDate ? isSameDay(deadlineDate, selectedDate) : false;
      const passesFilter = deadlineCategories[1].filterFn ? deadlineCategories[1].filterFn(tender) : true;
      return matchesDate && passesFilter;
    });
    
    // Filter tenders for additional info deadlines on the selected date
    itemsByCategory.additional = tenders.filter(tender => {
      const deadlineDate = deadlineCategories[2].extractDate(tender);
      return deadlineDate ? isSameDay(deadlineDate, selectedDate) : false;
    });
    
    // Filter tasks for the selected date
    itemsByCategory.task = tasks.filter(task => {
      const taskDate = deadlineCategories[3].extractDate(task);
      return taskDate ? isSameDay(taskDate, selectedDate) : false;
    });

    setSelectedDateItems(itemsByCategory);
  };

  // Handle date selection
  const handleSelect = (selectedDate: Date | undefined) => {
    setDate(selectedDate);
    if (selectedDate) {
      updateSelectedDateItems(selectedDate);
    } else {
      setSelectedDateItems({
        tender: [],
        application: [],
        additional: [],
        task: []
      });
    }
  };

  // Get the procedure type from a tender
  const getProcedureType = (tender: any): string => {
    if (!tender || !tender.tender_data) return "Unknown";
    
    const procedureData = tender.tender_data["procedure-type"] || 
                          tender.tender_data["TOP_TYPE"] || 
                          tender.tender_data.procedure?.type;
    
    if (procedureData) {
      if (typeof procedureData === 'string') {
        return procedureData;
      } else if (Array.isArray(procedureData) && procedureData.length > 0) {
        return procedureData[0];
      }
    }
    
    return "Unknown procedure";
  };

  // Get the title from a tender object with line break for better display
  const getTenderTitle = (tender: any) => {
    if (tender.tender_data) {
      if (tender.tender_data.match_reasons?.["notice-title"]?.eng) {
        const title = tender.tender_data.match_reasons["notice-title"].eng;
        return formatTitleWithLineBreak(title);
      }
      
      if (tender.tender_data.match_reasons?.notice_title?.eng?.[0]) {
        const title = tender.tender_data.match_reasons.notice_title.eng[0];
        return formatTitleWithLineBreak(title);
      }
      
      if (tender.tender_data["notice-title"]?.eng) {
        const title = tender.tender_data["notice-title"].eng;
        return formatTitleWithLineBreak(title);
      }
      
      if (tender.tender_data.title) {
        return formatTitleWithLineBreak(tender.tender_data.title);
      }
    }
    
    if (tender.title) {
      return formatTitleWithLineBreak(tender.title);
    }
    
    return "Untitled Tender";
  };

  // Format title with line break for better display
  const formatTitleWithLineBreak = (title: string) => {
    if (!title || title.length <= 40) return title;
    
    const breakPoint = title.substring(30, 50).indexOf(' ');
    if (breakPoint >= 0) {
      const firstPart = title.substring(0, 30 + breakPoint);
      const secondPart = title.substring(30 + breakPoint + 1);
      return `${firstPart}\n${secondPart}`;
    }
    
    return title;
  };

  // Get organization name from tender data with priority to organisation-name-buyer
  const getOrganizationName = (tender: any) => {
    if (!tender || !tender.tender_data) return "Unknown Organization";

    const data = tender.tender_data;

    if (data["organisation-name-buyer"]) {
      const orgNameBuyer = data["organisation-name-buyer"];
      
      if (typeof orgNameBuyer === 'object' && orgNameBuyer !== null) {
        const langs = Object.keys(orgNameBuyer);
        if (langs.length > 0) {
          const orgValue = orgNameBuyer[langs[0]];
          if (Array.isArray(orgValue) && orgValue.length > 0) {
            return orgValue[0];
          } else if (typeof orgValue === 'string') {
            return orgValue;
          }
        }
      } else if (Array.isArray(orgNameBuyer) && orgNameBuyer.length > 0) {
        return orgNameBuyer[0];
      } else if (typeof orgNameBuyer === 'string') {
        return orgNameBuyer;
      }
    }
    
    if (data.organization_name) {
      return String(data.organization_name);
    }
    
    if (isObject(data.match_reasons)) {
      if (isObject(data.match_reasons["buyer-name"])) {
        if ("eng" in data.match_reasons["buyer-name"] && data.match_reasons["buyer-name"].eng !== undefined) {
          const engValue = data.match_reasons["buyer-name"].eng;
          if (Array.isArray(engValue) && engValue.length > 0) {
            return String(engValue[0]);
          } else if (typeof engValue === 'string') {
            return String(engValue);
          }
        }
        
        const langs = Object.keys(data.match_reasons["buyer-name"]);
        if (langs.length > 0) {
          const langValue = data.match_reasons["buyer-name"][langs[0]];
          if (Array.isArray(langValue) && langValue.length > 0) {
            return String(langValue[0]);
          } else if (typeof langValue === 'string') {
            return String(langValue);
          }
        }
      }
    }
    
    if (data["buyer-name"]) {
      const buyerNameObj = data["buyer-name"];
      
      if (typeof buyerNameObj === 'object' && buyerNameObj !== null) {
        if ("eng" in buyerNameObj && buyerNameObj.eng !== undefined) {
          const engValue = buyerNameObj.eng;
          if (Array.isArray(engValue) && engValue.length > 0) {
            return String(engValue[0]);
          } else if (typeof engValue === 'string') {
            return String(engValue);
          }
        }
        
        const langs = Object.keys(buyerNameObj);
        if (langs.length > 0) {
          const langValue = buyerNameObj[langs[0]];
          if (Array.isArray(langValue) && langValue.length > 0) {
            return String(langValue[0]);
          } else if (typeof langValue === 'string') {
            return String(langValue);
          }
        }
      } else if (typeof buyerNameObj === 'string') {
        return String(buyerNameObj);
      }
    }
    
    if (data["organisation-name-buyer"]) {
      const orgName = data["organisation-name-buyer"];
      if (typeof orgName === 'object' && !Array.isArray(orgName)) {
        if (orgName.eng) {
          if (Array.isArray(orgName.eng) && orgName.eng.length > 0) {
            return orgName.eng[0];
          }
          return orgName.eng;
        }
        const langs = Object.keys(orgName);
        if (langs.length > 0) {
          const firstLangValue = orgName[langs[0]];
          if (Array.isArray(firstLangValue) && firstLangValue.length > 0) {
            return firstLangValue[0];
          }
          if (firstLangValue) {
            return firstLangValue;
          }
        }
      }
    }
    
    if (data.AU !== undefined) {
      if (typeof data.AU === 'object' && data.AU !== null) {
        const langs = Object.keys(data.AU);
        if (langs.length > 0 && Array.isArray(data.AU[langs[0]])) {
          return String(data.AU[langs[0]][0] || "Unknown Organization");
        }
      }
      return String(data.AU);
    }
    
    return "Unknown Organization";
  };

  // Helper function to count items by category
  const getCategoryCount = (category: DeadlineCategory) => {
    return selectedDateItems[category].length;
  };
  
  // Calculate total deadlines for the selected date
  const getTotalDeadlinesCount = () => {
    return Object.values(selectedDateItems).reduce(
      (total, items) => total + items.length, 
      0
    );
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <Card className="p-5">
        <h2 className="text-lg font-semibold mb-4">{t("dashboard.calendar.title", "Kalender")}</h2>
        <div className="flex justify-center w-full">
          <Calendar
            mode="single"
            selected={date}
            onSelect={handleSelect}
            className="w-full max-w-full rounded-md border pointer-events-auto"
            modifiers={{
              tenderDeadline: getCategoryDates('tender'),
              applicationDeadline: getCategoryDates('application'),
              additionalInfoDeadline: getCategoryDates('additional'),
              taskDeadline: getCategoryDates('task')
            }}
            modifiersStyles={{
              tenderDeadline: { 
                fontWeight: 'bold',
                backgroundColor: 'rgba(239, 68, 68, 0.1)', // red-500 with opacity
                color: 'rgb(239, 68, 68)' // red-500
              },
              applicationDeadline: {
                fontWeight: 'bold',
                backgroundColor: 'rgba(59, 130, 246, 0.1)', // blue-500 with opacity
                color: 'rgb(59, 130, 246)' // blue-500
              },
              additionalInfoDeadline: {
                fontWeight: 'bold',
                backgroundColor: 'rgba(245, 158, 11, 0.1)', // amber-500 with opacity
                color: 'rgb(245, 158, 11)' // amber-500
              },
              taskDeadline: {
                fontWeight: 'bold',
                backgroundColor: 'rgba(34, 197, 94, 0.1)', // green-500 with opacity
                color: 'rgb(34, 197, 94)' // green-500
              }
            }}
            styles={{
              caption: { width: '100%' },
              month: { width: '100%' },
              table: { width: '100%' },
              head_row: { width: '100%', justifyContent: 'space-around' },
              row: { width: '100%', justifyContent: 'space-around' },
              day: { margin: '0', width: '34px', height: '34px' },
              nav: { margin: '0' }
            }}
          />
        </div>
        
        {/* Calendar Legend */}
        <div className="mt-3 grid grid-cols-2 gap-2">
          {deadlineCategories.map((category) => (
            <div key={category.key} className="flex items-center gap-1 text-xs">
              <div className={`w-3 h-3 rounded-full ${category.color.split(' ')[0]}`}></div>
              <span className="text-muted-foreground">{category.label}</span>
            </div>
          ))}
        </div>
      </Card>

      <Card className="p-5">
        <h2 className="text-lg font-semibold mb-3">
          {date 
            ? `${t("dashboard.calendar.deadline", "Deadline")} ${formatDate(date.toISOString())}` 
            : t("dashboard.calendar.selectDate", "Vælg en dato")}
        </h2>
        
        {getTotalDeadlinesCount() > 0 ? (
          <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as DeadlineCategory | 'all')}>
            <TabsList className="mb-4 w-full">
              <TabsTrigger value="all" className="text-xs">
                {t("dashboard.calendar.allCategories", "Alle")}
                <Badge variant="secondary" className="ml-1">{getTotalDeadlinesCount()}</Badge>
              </TabsTrigger>
              
              {deadlineCategories.map((category) => {
                const count = getCategoryCount(category.key);
                return count > 0 ? (
                  <TabsTrigger key={category.key} value={category.key} className="text-xs">
                    {category.label.split(' ')[0]}
                    <Badge variant="secondary" className="ml-1">{count}</Badge>
                  </TabsTrigger>
                ) : null;
              })}
            </TabsList>
            
            <TabsContent value="all">
              <ScrollArea className="h-[300px] pr-3">
                {deadlineCategories.map((category) => {
                  const items = selectedDateItems[category.key];
                  if (items.length === 0) return null;
                  
                  return (
                    <div key={category.key} className="mb-5 last:mb-0">
                      <h3 className="font-medium text-sm text-gray-500 flex items-center gap-1 mb-3">
                        {category.icon}
                        {category.label} ({items.length})
                      </h3>
                      
                      <div className="space-y-3">
                        {category.key === 'task' ? (
                          // Render tasks
                          items.map((task, index) => (
                            <Button
                              key={`task-${index}`}
                              variant="outline"
                              className={`w-full justify-between p-3 h-auto border ${category.color.split(' ')[2]}`}
                              onClick={() => handleTaskClick(task)}
                            >
                              <div className="flex-1 text-left overflow-hidden mr-2">
                                <h3 className="font-medium mb-1.5 line-clamp-1" title={task.title}>{task.title}</h3>
                                <div className="text-sm text-gray-600 flex flex-col space-y-1">
                                  <div className="flex items-start">
                                    <span className="font-medium w-32 inline-block flex-shrink-0">{t("dashboard.calendar.assignedTo", "Tildelt til")}:</span>
                                    <span className="line-clamp-1" title={task.assignee?.company_name || task.assignee?.email || t("dashboard.calendar.unassigned", "Ikke tildelt")}>
                                      {task.assignee?.company_name || task.assignee?.email || t("dashboard.calendar.unassigned", "Ikke tildelt")}
                                    </span>
                                  </div>
                                  <div className="flex items-start">
                                    <span className="font-medium w-32 inline-block flex-shrink-0">{t("dashboard.calendar.dueDate", "Forfaldsdato")}:</span>
                                    <span className="whitespace-nowrap">
                                      {formatDate(task.due_date)}
                                    </span>
                                  </div>
                                  <div className="flex items-start">
                                    <span className="font-medium w-32 inline-block flex-shrink-0">{t("dashboard.calendar.priority", "Prioritet")}:</span>
                                    <span className="whitespace-nowrap">
                                      {task.priority}
                                    </span>
                                  </div>
                                </div>
                              </div>
                              <ExternalLink className="h-4 w-4 flex-shrink-0 ml-1" />
                            </Button>
                          ))
                        ) : (
                          // Render tenders
                          items.map((tender, index) => {
                            const deadlineDate = category.extractDate(tender);
                            const procedureType = getProcedureType(tender);
                            
                            return (
                              <Button
                                key={`${category.key}-${index}`}
                                variant="outline"
                                className={`w-full justify-between p-3 h-auto border ${category.color.split(' ')[2]}`}
                                onClick={() => handleTenderClick(tender)}
                              >
                                <div className="flex-1 text-left overflow-hidden mr-2">
                                  <h3 className="font-medium mb-1.5 break-words whitespace-pre-line" title={getTenderTitle(tender)}>
                                    {getTenderTitle(tender)}
                                  </h3>
                                  <div className="text-sm text-gray-600 flex flex-col space-y-1">
                                    <div className="flex items-start">
                                      <span className="font-medium w-32 inline-block flex-shrink-0">{t("dashboard.calendar.organization", "Organisation")}:</span>
                                      <span className="line-clamp-1" title={getOrganizationName(tender)}>
                                        {getOrganizationName(tender)}
                                      </span>
                                    </div>
                                    <div className="flex items-start">
                                      <span className="font-medium w-32 inline-block flex-shrink-0">
                                        {category.key === 'tender' 
                                          ? t("dashboard.calendar.tenderDeadline", "Tilbudsfrist") 
                                          : category.key === 'application'
                                            ? t("dashboard.calendar.applicationDeadline", "Ansøgningsfrist")
                                            : t("dashboard.calendar.additionalInfoDeadline", "Informationsfrist")}:
                                      </span>
                                      <span className="whitespace-nowrap">
                                        {deadlineDate ? formatDate(deadlineDate.toISOString()) : t("dashboard.calendar.notSpecified", "Ikke angivet")}
                                      </span>
                                    </div>
                                    <div className="flex items-start">
                                      <span className="font-medium w-32 inline-block flex-shrink-0">{t("dashboard.calendar.procedure", "Procedure")}:</span>
                                      <span className="line-clamp-1" title={procedureType}>
                                        {procedureType}
                                      </span>
                                    </div>
                                    <div className="flex items-start">
                                      <span className="font-medium w-32 inline-block flex-shrink-0">{t("dashboard.calendar.value", "Værdi")}:</span>
                                      <span className="whitespace-nowrap">
                                        {tender.tender_data?.value ? `€${tender.tender_data.value}` : t("dashboard.calendar.notSpecified", "Ikke angivet")}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                                <ExternalLink className="h-4 w-4 flex-shrink-0 ml-1" />
                              </Button>
                            );
                          })
                        )}
                      </div>
                    </div>
                  );
                })}
              </ScrollArea>
            </TabsContent>
            
            {/* Individual category tabs */}
            {deadlineCategories.map((category) => {
              const items = selectedDateItems[category.key];
              return (
                <TabsContent key={category.key} value={category.key}>
                  <ScrollArea className="h-[300px] pr-3">
                    {items.length > 0 ? (
                      <div className="space-y-3">
                        {category.key === 'task' ? (
                          // Render tasks
                          items.map((task, index) => (
                            <Button
                              key={`task-${index}`}
                              variant="outline"
                              className={`w-full justify-between p-3 h-auto border ${category.color.split(' ')[2]}`}
                              onClick={() => handleTaskClick(task)}
                            >
                              <div className="flex-1 text-left overflow-hidden mr-2">
                                <h3 className="font-medium mb-1.5 line-clamp-1" title={task.title}>{task.title}</h3>
                                <div className="text-sm text-gray-600 flex flex-col space-y-1">
                                  <div className="flex items-start">
                                    <span className="font-medium w-32 inline-block flex-shrink-0">{t("dashboard.calendar.assignedTo", "Tildelt til")}:</span>
                                    <span className="line-clamp-1" title={task.assignee?.company_name || task.assignee?.email || t("dashboard.calendar.unassigned", "Ikke tildelt")}>
                                      {task.assignee?.company_name || task.assignee?.email || t("dashboard.calendar.unassigned", "Ikke tildelt")}
                                    </span>
                                  </div>
                                  <div className="flex items-start">
                                    <span className="font-medium w-32 inline-block flex-shrink-0">{t("dashboard.calendar.dueDate", "Forfaldsdato")}:</span>
                                    <span className="whitespace-nowrap">
                                      {formatDate(task.due_date)}
                                    </span>
                                  </div>
                                  <div className="flex items-start">
                                    <span className="font-medium w-32 inline-block flex-shrink-0">{t("dashboard.calendar.priority", "Prioritet")}:</span>
                                    <span className="whitespace-nowrap">
                                      {task.priority}
                                    </span>
                                  </div>
                                </div>
                              </div>
                              <ExternalLink className="h-4 w-4 flex-shrink-0 ml-1" />
                            </Button>
                          ))
                        ) : (
                          // Render tenders
                          items.map((tender, index) => {
                            const deadlineDate = category.extractDate(tender);
                            const procedureType = getProcedureType(tender);

                            return (
                              <Button
                                key={`${category.key}-${index}`}
                                variant="outline"
                                className={`w-full justify-between p-3 h-auto border ${category.color.split(' ')[2]}`}
                                onClick={() => handleTenderClick(tender)}
                              >
                                <div className="flex-1 text-left overflow-hidden mr-2">
                                  <h3 className="font-medium mb-1.5 break-words whitespace-pre-line" title={getTenderTitle(tender)}>
                                    {getTenderTitle(tender)}
                                  </h3>
                                  <div className="text-sm text-gray-600 flex flex-col space-y-1">
                                    <div className="flex items-start">
                                      <span className="font-medium w-32 inline-block flex-shrink-0">{t("dashboard.calendar.organization", "Organisation")}:</span>
                                      <span className="line-clamp-1" title={getOrganizationName(tender)}>
                                        {getOrganizationName(tender)}
                                      </span>
                                    </div>
                                    <div className="flex items-start">
                                      <span className="font-medium w-32 inline-block flex-shrink-0">
                                        {category.key === 'tender' 
                                          ? t("dashboard.calendar.tenderDeadline", "Tilbudsfrist") 
                                          : category.key === 'application'
                                            ? t("dashboard.calendar.applicationDeadline", "Ansøgningsfrist")
                                            : t("dashboard.calendar.additionalInfoDeadline", "Informationsfrist")}:
                                      </span>
                                      <span className="whitespace-nowrap">
                                        {deadlineDate ? formatDate(deadlineDate.toISOString()) : t("dashboard.calendar.notSpecified", "Ikke angivet")}
                                      </span>
                                    </div>
                                    <div className="flex items-start">
                                      <span className="font-medium w-32 inline-block flex-shrink-0">{t("dashboard.calendar.procedure", "Procedure")}:</span>
                                      <span className="line-clamp-1" title={procedureType}>
                                        {procedureType}
                                      </span>
                                    </div>
                                    <div className="flex items-start">
                                      <span className="font-medium w-32 inline-block flex-shrink-0">{t("dashboard.calendar.value", "Værdi")}:</span>
                                      <span className="whitespace-nowrap">
                                        {tender.tender_data?.value ? `€${tender.tender_data.value}` : t("dashboard.calendar.notSpecified", "Ikke angivet")}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                                <ExternalLink className="h-4 w-4 flex-shrink-0 ml-1" />
                              </Button>
                            );
                          })
                        )}
                      </div>
                    ) : (
                      <div className="text-center text-gray-500 py-8">
                        {t("dashboard.calendar.noDeadlinesOfType", "Ingen frister af denne type på denne dato")}
                      </div>
                    )}
                  </ScrollArea>
                </TabsContent>
              );
            })}
          </Tabs>
        ) : (
          <div className="text-center text-gray-500 py-8">
            {date ? t("dashboard.calendar.noDeadlines", "Ingen deadlines på denne dato") : t("dashboard.calendar.selectDate", "Vælg en dato")}
          </div>
        )}
      </Card>
    </div>
  );
};
